import * as React from "react";
import { ButtonProps as ButtonBaseProps } from "@skbkontur/react-ui";
import { AllIconNames, Button as BaseButton, Hint } from "react-ui";

import { Tooltip } from "../../Internals/Tooltip/Tooltip";
import { AbsoluteModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { BUTTON_CLICK } from "../../metricsAction";
import { useMetrics } from "../../Internals/Metrics/MetricsProviderContext";
import { ControlValidationProps } from "../../Validations/ValidationProps";
import { useTestPageObject } from "../../Services/TestPageObjects/PageObjectExposure";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { emptyRef } from "../../TypingUtils";
import { tryScrollIntoViewWithFallback } from "../../../Helpers/BrowserHelpers";
import { IconConverter } from "../../Icons/Icon";

import { ButtonTestPageObject } from "./ButtonTestPageObject";
import { ButtonWrapper } from "./Button.styles";

type ButtonIcon = AllIconNames | React.ReactElement;

type ButtonBasePropsForOverride = Omit<ButtonBaseProps, "error" | "warning" | "icon" | "rightIcon">;

export interface ButtonProps extends ControlValidationProps, ButtonBasePropsForOverride {
    bindingPath?: AbsoluteModelPath;
    value?: string;
    hint?: React.ReactNode;
    icon?: ButtonIcon;
    rightIcon?: ButtonIcon;
    onClick: () => void;
}

export function Button(props: ButtonProps): JSX.Element {
    const track = useMetrics();
    const rootRef = React.useRef<HTMLDivElement>(emptyRef);

    const { children, bindingPath, value, hint, error, warning, icon, rightIcon, ...restProps } = props;
    const tooltipMessage = error || warning;

    const handleClick = () => {
        track(BUTTON_CLICK, { value: value, path: bindingPath, type: restProps.type });
        props.onClick();
    };

    const handleFocusRequest = (): void => {
        if (rootRef.current != undefined) {
            tryScrollIntoViewWithFallback(rootRef.current, { block: "center", inline: "center" });
        }
    };

    useTestPageObject(() => ButtonTestPageObject(props, handleClick), [value, error, warning, children, bindingPath]);

    return (
        <FocusManagementObserver onRequestFocus={handleFocusRequest}>
            <Hint text={tooltipMessage ? undefined : hint}>
                <Tooltip pos="bottom left" trigger={tooltipMessage ? "hover" : "closed"} value={tooltipMessage}>
                    <ButtonWrapper ref={rootRef}>
                        <BaseButton
                            {...restProps}
                            icon={icon ? <IconConverter icon={icon} /> : undefined}
                            rightIcon={rightIcon ? <IconConverter icon={rightIcon} /> : undefined}
                            onClick={handleClick}
                            error={Boolean(error)}
                            warning={Boolean(warning)}
                            loading={props.loading}>
                            {value ? value : children}
                        </BaseButton>
                    </ButtonWrapper>
                </Tooltip>
            </Hint>
        </FocusManagementObserver>
    );
}
